/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-FormattedMessage */

/*
This component represents the help center list item in the editor help menu
(currently in the bottom left of the editor view).

It is broken out as a component because of the need to add/remove a listener in
componentDidUpdate.
 */

import React, { type FC, useContext } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { DropdownItem as AkDropdownItem } from '@atlaskit/dropdown-menu';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';

import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext';
import { fg } from '@confluence/feature-gating';

import type { HelpCenterStateContainer } from './HelpCenterStateContainers';
import { HelpMenuClassicContext } from './HelpMenuClassicContext';
import { HELP_MENU_SPOTLIGHT } from './constants';
import { useHelpMenuState } from './useHelpMenuState';

type AnyFunctionType = (...args: any[]) => any;

export type HelpMenuHelpCenterItemProps = {
	helpCenterState: HelpCenterStateContainer;
	closeHelpMenu: AnyFunctionType;
};

type HelpMenuHelpCenterItemContextProps = {
	Changeboarding: any;
};

const HelpMenuHelpCenterItemWithContext = (
	props: HelpMenuHelpCenterItemProps & WithAnalyticsEventsProps,
) => {
	const classicEditorContext = useContext(ClassicEditorContext);
	const helpMenuClassicContext = useContext(HelpMenuClassicContext);

	// The next editor uses the ClassicEditorContext, while classic uses the HelpMenuClassicContext
	const { Changeboarding } = classicEditorContext.Changeboarding
		? classicEditorContext
		: helpMenuClassicContext!;

	return <HelpMenuHelpCenterItemClass {...props} Changeboarding={Changeboarding} />;
};

const HelpMenuHelpCenterItemClass: FC<
	HelpMenuHelpCenterItemProps & WithAnalyticsEventsProps & HelpMenuHelpCenterItemContextProps
> = ({ helpCenterState, closeHelpMenu, createAnalyticsEvent, Changeboarding }) => {
	const [helpCenterSweetState, { openHelpCenter, hideHelpMenuSpotlight }] = useHelpMenuState();

	const onOpenHelpCenter = () => {
		if (fg('confluence_frontend_editor_help_center_performance')) {
			openHelpCenter();
		} else {
			void helpCenterState.open(true);
		}

		closeHelpMenu();

		createAnalyticsEvent!({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'helpMenuItemForEditorHelpCenter',
				source: 'editorHelpMenu',
			},
		}).fire();
	};

	/**
	 * When the last changeboard in the current sequence is closed, call this
	 * callback to set the state of the spotlight in helpCenterState.
	 * @param helpCenterState
	 */
	const changeboardingFinished = () => {
		if (fg('confluence_frontend_editor_help_center_performance')) {
			hideHelpMenuSpotlight();
		} else {
			void helpCenterState.hideHelpMenuSpotlight();
		}

		closeHelpMenu();

		createAnalyticsEvent!({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'spotlightForEditorHelpCenter',
				source: 'editor help menu spotlight',
			},
		}).fire();
	};

	const shouldShowHelpMenuSpotlight = fg('confluence_frontend_editor_help_center_performance')
		? helpCenterSweetState.shouldShowHelpMenuSpotlight
		: helpCenterState.state.showHelpMenuSpotlight;

	const isHelpCenterOpen = fg('confluence_frontend_editor_help_center_performance')
		? helpCenterSweetState.isHelpCenterOpen
		: helpCenterState.state.helpCenterOpen;

	return shouldShowHelpMenuSpotlight ? (
		<Changeboarding
			spotlightId={HELP_MENU_SPOTLIGHT}
			delaySpotlight={200}
			changeboardFlowFinished={changeboardingFinished}
		>
			<AkDropdownItem isDisabled={isHelpCenterOpen} onClick={onOpenHelpCenter}>
				<FormattedMessage id="fabric.help.center.menuitem.title" />
			</AkDropdownItem>
		</Changeboarding>
	) : (
		<AkDropdownItem
			testId="help-center-menuitem-title"
			isDisabled={isHelpCenterOpen}
			onClick={onOpenHelpCenter}
		>
			<FormattedMessage id="fabric.help.center.menuitem.title" />
		</AkDropdownItem>
	);
};

export const HelpMenuHelpCenterItem = withAnalyticsEvents()(HelpMenuHelpCenterItemWithContext);
